// referralIntros.ts — bilingual instruction strings for the
// referral card on the player Profile page.
//
// Each entry is used in TWO places:
//   1. Rendered as visible text inside the referral card so the
//      player can READ the instructions in their preferred
//      language.
//   2. Spoken aloud by the ear-icon button, via Web Speech API —
//      same text, same language, accessible to players who can't
//      read or who just prefer to listen.
//
// Selection rule: if the player has set a secondary language in
// Profile → Languages, that wins (display + audio). Otherwise we
// fall back to the current UI language, then to English.
//
// One entry per supported UI language. The BCP-47 code is the hint
// we pass to SpeechSynthesisUtterance.lang so the OS picks a
// matching voice; if no voice for that locale is installed on the
// device, the OS falls back to whatever it can synthesize but the
// lang attribute still helps pronunciation rules. Indian variants
// ('-IN') are preferred where they exist.
//
// Keep each script short (2 sentences, ~20 seconds spoken / ~3
// lines on a phone). The goal is to teach how to use it, not to
// recite marketing copy.

import type { Lang } from './translations';

export type ReferralIntro = {
  /** BCP-47 locale tag passed to SpeechSynthesisUtterance.lang. */
  bcp47: string;
  /** What the OS speaks. Plain text — no SSML. */
  text: string;
};

export const REFERRAL_INTROS: Record<Lang, ReferralIntro> = {
  en: {
    bcp47: 'en-IN',
    text:
      'Share your referral code with your friends. When they sign up ' +
      'using your code and recharge their wallet, you will automatically ' +
      'earn a bonus in your wallet.',
  },
  hi: {
    bcp47: 'hi-IN',
    text:
      'अपना रेफ़रल कोड अपने दोस्तों के साथ साझा करें। ' +
      'जब वे आपके कोड से साइन अप करके अपना वॉलेट रिचार्ज करेंगे, ' +
      'तो आपको अपने वॉलेट में अपने आप बोनस मिल जाएगा।',
  },
  bn: {
    bcp47: 'bn-IN',
    text:
      'আপনার রেফারেল কোড আপনার বন্ধুদের সঙ্গে শেয়ার করুন। ' +
      'যখন তারা আপনার কোড ব্যবহার করে সাইন আপ করে তাদের ওয়ালেট রিচার্জ করবে, ' +
      'তখন আপনি স্বয়ংক্রিয়ভাবে আপনার ওয়ালেটে বোনাস পাবেন।',
  },
  mr: {
    bcp47: 'mr-IN',
    text:
      'तुमचा रेफरल कोड तुमच्या मित्रांसोबत शेअर करा. ' +
      'जेव्हा ते तुमच्या कोडने साइन अप करतील आणि त्यांचे वॉलेट रिचार्ज करतील, ' +
      'तेव्हा तुम्हाला तुमच्या वॉलेटमध्ये आपोआप बोनस मिळेल.',
  },
  ta: {
    bcp47: 'ta-IN',
    text:
      'உங்கள் பரிந்துரை குறியீட்டை உங்கள் நண்பர்களுடன் பகிர்ந்து கொள்ளுங்கள். ' +
      'அவர்கள் உங்கள் குறியீட்டைப் பயன்படுத்தி பதிவு செய்து தங்கள் பணப்பையை ரீசார்ஜ் செய்யும் போது, ' +
      'உங்கள் பணப்பையில் தானாகவே போனஸ் கிடைக்கும்.',
  },
  te: {
    bcp47: 'te-IN',
    text:
      'మీ రిఫరల్ కోడ్‌ను మీ స్నేహితులతో పంచుకోండి. ' +
      'వారు మీ కోడ్‌తో సైన్ అప్ చేసి తమ వాలెట్‌ను రీచార్జ్ చేసినప్పుడు, ' +
      'మీకు మీ వాలెట్‌లో స్వయంచాలకంగా బోనస్ లభిస్తుంది.',
  },
  gu: {
    bcp47: 'gu-IN',
    text:
      'તમારો રેફરલ કોડ તમારા મિત્રો સાથે શેર કરો. ' +
      'જ્યારે તેઓ તમારા કોડથી સાઇન અપ કરીને તેમના વોલેટને રીચાર્જ કરશે, ' +
      'ત્યારે તમારા વોલેટમાં આપોઆપ બોનસ મળશે.',
  },
  ur: {
    bcp47: 'ur-IN',
    text:
      'اپنا ریفرل کوڈ اپنے دوستوں کے ساتھ شیئر کریں۔ ' +
      'جب وہ آپ کے کوڈ سے سائن اپ کرکے اپنا والیٹ ریچارج کریں گے، ' +
      'تو آپ کو آپ کے والیٹ میں خودبخود بونس مل جائے گا۔',
  },
  kn: {
    bcp47: 'kn-IN',
    text:
      'ನಿಮ್ಮ ರೆಫರಲ್ ಕೋಡ್ ಅನ್ನು ನಿಮ್ಮ ಸ್ನೇಹಿತರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಿ. ' +
      'ಅವರು ನಿಮ್ಮ ಕೋಡ್‌ನೊಂದಿಗೆ ಸೈನ್ ಅಪ್ ಮಾಡಿ ತಮ್ಮ ವಾಲೆಟ್ ಅನ್ನು ರೀಚಾರ್ಜ್ ಮಾಡಿದಾಗ, ' +
      'ನಿಮ್ಮ ವಾಲೆಟ್‌ನಲ್ಲಿ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಬೋನಸ್ ಸಿಗುತ್ತದೆ.',
  },
  or: {
    bcp47: 'or-IN',
    text:
      'ଆପଣଙ୍କ ରେଫରାଲ କୋଡ଼କୁ ଆପଣଙ୍କ ବନ୍ଧୁମାନଙ୍କ ସହିତ ଅଂଶୀଦାର କରନ୍ତୁ। ' +
      'ସେମାନେ ଆପଣଙ୍କ କୋଡ଼ ବ୍ୟବହାର କରି ସାଇନ ଅପ କରି ସେମାନଙ୍କ ୱାଲେଟ ରିଚାର୍ଜ କଲେ, ' +
      'ଆପଣଙ୍କ ୱାଲେଟରେ ସ୍ୱୟଂଚାଳିତ ଭାବରେ ବୋନସ ମିଳିବ।',
  },
  pa: {
    bcp47: 'pa-IN',
    text:
      'ਆਪਣਾ ਰੈਫਰਲ ਕੋਡ ਆਪਣੇ ਦੋਸਤਾਂ ਨਾਲ ਸਾਂਝਾ ਕਰੋ। ' +
      'ਜਦੋਂ ਉਹ ਤੁਹਾਡੇ ਕੋਡ ਨਾਲ ਸਾਈਨ ਅੱਪ ਕਰਕੇ ਆਪਣਾ ਵਾਲਿਟ ਰੀਚਾਰਜ ਕਰਨਗੇ, ' +
      'ਤਾਂ ਤੁਹਾਨੂੰ ਆਪਣੇ ਵਾਲਿਟ ਵਿੱਚ ਆਪਣੇ ਆਪ ਬੋਨਸ ਮਿਲ ਜਾਵੇਗਾ।',
  },
  ml: {
    bcp47: 'ml-IN',
    text:
      'നിങ്ങളുടെ റഫറൽ കോഡ് നിങ്ങളുടെ സുഹൃത്തുക്കളുമായി പങ്കിടുക. ' +
      'അവർ നിങ്ങളുടെ കോഡ് ഉപയോഗിച്ച് സൈൻ അപ്പ് ചെയ്ത് അവരുടെ വാലറ്റ് റീചാർജ് ചെയ്യുമ്പോൾ, ' +
      'നിങ്ങൾക്ക് നിങ്ങളുടെ വാലറ്റിൽ സ്വയമേവ ഒരു ബോണസ് ലഭിക്കും.',
  },
};
